import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Container, Typography, Grid, Link, MenuItem, Checkbox, ListItemText, ListSubheader } from '@mui/material';
import { SelectMenu } from '@livable/saas-react-components';
import useStyles from '../styles';
const singleSelectExample = "\nimport { SelectMenu } from '@livable/saas-react-components';\n\n<SelectMenu\n  id=\"age-selector\"\n  value={age}\n  label=\"Age\"\n  labelId=\"age-selector-label\"\n  onChange={handleAgeChange}\n  addActionButton={React.node} // custom action button\n  enableAddAction\n  enableSearch\n>\n  <MenuItem value={10}>Ten</MenuItem>\n</SelectMenu>\n";
const multipleSelectExample = "\nimport { SelectMenu } from '@livable/saas-react-components';\n\n<SelectMenu\n  id=\"friends-selector\"\n  value={friends}\n  label=\"Friends\"\n  labelId=\"friends-selector-label\"\n  onChange={handleFriendsChange}\n  addActionButton={React.node} // custom action button\n  multiple\n  renderValue={(selected) => selected.join(\", \")}\n  enableAddAction\n  enableSearch\n>\n  <MenuItem key=\"select-all\" value={\"} variant=\"multiple\">\n    <Checkbox />\n    <ListItemText>Select all [attribute]</ListItemText>\n  </MenuItem>\n  {names.map((name) => (\n    <MenuItem key={name} value={name} variant=\"multiple\">\n      <Checkbox checked={friends.indexOf(name) > -1} />\n      <ListItemText primary={name} />\n    </MenuItem>\n  ))}\n</SelectMenu>\n";
const emptySelectExample = "\nimport { SelectMenu } from '@livable/saas-react-components';\n\n<SelectMenu\n  id=\"empty-selector\"\n  value={\"}\n  labelId=\"empty-selector-label\"\n  label=\"Empty\"\n  addActionButton={React.node} // custom action button\n  enableSearch\n  enableAddAction\n>\n  <ListSubheader>\n    Nothing found. You can add your own input\n  </ListSubheader>\n</SelectMenu>\n";
function SingleSelect(_ref) {
  let _ref$enableAddAction = _ref.enableAddAction,
    enableAddAction = _ref$enableAddAction === void 0 ? false : _ref$enableAddAction,
    _ref$enableSearch = _ref.enableSearch,
    enableSearch = _ref$enableSearch === void 0 ? false : _ref$enableSearch;
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    age = _useState2[0],
    setAge = _useState2[1];
  const handleAgeChange = event => {
    if (event.target.value) setAge(event.target.value);
  };
  return /*#__PURE__*/React.createElement(SelectMenu, {
    id: "age-selector",
    value: age,
    label: "Age",
    labelId: "age-selector-label",
    onChange: handleAgeChange,
    enableAddAction: enableAddAction,
    enableSearch: enableSearch
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: 10
  }, "Ten"), /*#__PURE__*/React.createElement(MenuItem, {
    value: 20
  }, "Twenty"), /*#__PURE__*/React.createElement(MenuItem, {
    value: 30
  }, "Thirty"));
}
function MultiSelect(_ref2) {
  let _ref2$enableAddAction = _ref2.enableAddAction,
    enableAddAction = _ref2$enableAddAction === void 0 ? false : _ref2$enableAddAction,
    _ref2$enableSearch = _ref2.enableSearch,
    enableSearch = _ref2$enableSearch === void 0 ? false : _ref2$enableSearch;
  const _React$useState = React.useState([]),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    friends = _React$useState2[0],
    setFriendName = _React$useState2[1];
  const handleFriendsChange = (event, child) => {
    if (child.props.value) setFriendName(event.target.value);
  };
  const names = ['Oliver Hansen Andersen', 'Van Henry', 'April Tucker', 'Ralph Hubbard'];
  return /*#__PURE__*/React.createElement(SelectMenu, {
    id: "friends-selector",
    value: friends,
    label: "Friends",
    labelId: "friends-selector-label",
    onChange: handleFriendsChange,
    multiple: true,
    renderValue: selected => selected.join(', '),
    enableAddAction: enableAddAction,
    enableSearch: enableSearch
  }, /*#__PURE__*/React.createElement(MenuItem, {
    key: "select-all",
    value: "",
    variant: "multiple"
  }, /*#__PURE__*/React.createElement(Checkbox, null), /*#__PURE__*/React.createElement(ListItemText, null, "Select all [attribute]")), names.map(name => /*#__PURE__*/React.createElement(MenuItem, {
    key: name,
    value: name,
    variant: "multiple"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: friends.indexOf(name) > -1
  }), /*#__PURE__*/React.createElement(ListItemText, {
    primary: name
  }))));
}
function EmptySelect(_ref3) {
  let _ref3$enableAddAction = _ref3.enableAddAction,
    enableAddAction = _ref3$enableAddAction === void 0 ? false : _ref3$enableAddAction,
    _ref3$enableSearch = _ref3.enableSearch,
    enableSearch = _ref3$enableSearch === void 0 ? false : _ref3$enableSearch;
  return /*#__PURE__*/React.createElement(SelectMenu, {
    id: "empty-selector",
    value: "",
    labelId: "empty-selector-label",
    label: "Empty",
    enableAddAction: enableAddAction,
    enableSearch: enableSearch
  }, /*#__PURE__*/React.createElement(ListSubheader, null, "Nothing found. You can add your own input"));
}
export default function LvSelects() {
  const classes = useStyles();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h1",
    className: classes.section
  }, /*#__PURE__*/React.createElement("span", null, "Dropdown selects "), /*#__PURE__*/React.createElement(Link, {
    href: "https://mui.com/material-ui/react-select/#main-content",
    target: "_blank"
  }, "(documentation)")), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h2",
    gutterBottom: true
  }, "Single Select"), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 24
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h4",
    gutterBottom: true
  }, "Single"), /*#__PURE__*/React.createElement(SingleSelect, null)), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h4",
    gutterBottom: true
  }, "Single + Search"), /*#__PURE__*/React.createElement(SingleSelect, {
    enableSearch: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h4",
    gutterBottom: true
  }, "Single + Action"), /*#__PURE__*/React.createElement(SingleSelect, {
    enableAddAction: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h4",
    gutterBottom: true
  }, "Single + Search + Action"), /*#__PURE__*/React.createElement(SingleSelect, {
    enableAddAction: true,
    enableSearch: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.codeSnippet
  }, singleSelectExample))), /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h2",
    gutterBottom: true
  }, "Multiple Select"), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 24
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h4",
    gutterBottom: true
  }, "Multiple"), /*#__PURE__*/React.createElement(MultiSelect, null)), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h4",
    gutterBottom: true
  }, "Multiple + Action"), /*#__PURE__*/React.createElement(MultiSelect, {
    enableAddAction: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h4",
    gutterBottom: true
  }, "Multiple + Search"), /*#__PURE__*/React.createElement(MultiSelect, {
    enableSearch: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h4",
    gutterBottom: true
  }, "Multiple + Search + Action"), /*#__PURE__*/React.createElement(MultiSelect, {
    enableSearch: true,
    enableAddAction: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.codeSnippet
  }, multipleSelectExample))), /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h2",
    gutterBottom: true
  }, "Empty Select"), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 24
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h4",
    gutterBottom: true
  }, "Empty"), /*#__PURE__*/React.createElement(EmptySelect, null)), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h4",
    gutterBottom: true
  }, "Empty + Action"), /*#__PURE__*/React.createElement(EmptySelect, {
    enableAddAction: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.subsectionName,
    variant: "h4",
    gutterBottom: true
  }, "Empty + Search +Action"), /*#__PURE__*/React.createElement(EmptySelect, {
    enableAddAction: true,
    enableSearch: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.codeSnippet
  }, emptySelectExample))))));
}
SingleSelect.propTypes = {
  enableAddAction: PropTypes.bool,
  enableSearch: PropTypes.bool
};
MultiSelect.propTypes = {
  enableAddAction: PropTypes.bool,
  enableSearch: PropTypes.bool
};
EmptySelect.propTypes = {
  enableAddAction: PropTypes.bool,
  enableSearch: PropTypes.bool
};